<template>
   <div class="container-fluid main-color">
    <!-- start page title -->
    <div class="row">
      <div class="col-12">
        <div class="page-title-box">
          <h4 class="page-title">Add Assessment Form</h4>
        </div>
      </div>
    </div>

    <!-- end page title -->
    <div class="row">
      <div class="col-lg-12">
        <place-holder v-if="loading"></place-holder>
        <div class="card">
          <div v-if="!loading" class="card-body">
            <form class="parsley-examples">
              <!-- Module Name -->
              <div class="row mb-3">
                <div class="col-md-3">
                  <label class="form-label fw-semibold">Module Name <span class="text-danger">*</span></label>
                </div>
                <div class="col-md-6">
                  <v-select
                    v-model="module.name"
                    label="name"
                    :options="moduleList"
                    :selectable="(options) => isSelectedModule(options)"
                    :clearable="false"
                    :class="{
                      'p-invalid': v$.module.name.$error,
                    }"
                  ></v-select>
                  <v-errors
                    :serverErrors="errorFor('name')"
                    :vuelidateErrors="{
                      errors: v$.module.name.$errors,
                      value: 'Module name',
                    }"
                  ></v-errors>
                </div>
              </div>

              <!-- <input
                type="hidden"
                class="form-control"
                value="30"
                v-model="module.assessment_duration"
                placeholder="Enter duration"
              /> -->

              <!-- Questions Section Header -->
              <div class="row mb-2">
                <div class="col-md-12">
                  <div class="pb-2">
                    <h5 class="mb-0 text-primary">Assessment Questions</h5>
                  </div>
                </div>
              </div>

              <!-- Questions List -->
              <div v-for="(question, index) in module.questions" :key="index" class="question-item mb-3">
                <!-- Question Text -->
                <div class="row mb-2">
                  <div class="col-md-3">
                    <label class="form-label fw-semibold">{{ index +1 }}. Question Name <span class="text-danger">*</span></label>
                  </div>
                  <div class="col-md-6">
                    <textarea
                      class="form-control"
                      v-model="question.question"
                      placeholder="Enter the question"
                      rows="2"
                      :class="{
                        'is-invalid': hasQuestionError(index, 'question'),
                      }"
                    ></textarea>
                    <v-errors
                      v-if="hasQuestionError(index, 'question')"
                      :vuelidateErrors="{
                        errors: getQuestionErrors(index, 'question'),
                        value: 'Question text',
                      }"
                    ></v-errors>
                  </div>
                </div>

                <div class="row mb-2 align-items-center">
                  <!-- Question Type -->
                  <div class="col-md-3">
                    <label class="form-label fw-semibold ms-2">Answer Type <span class="text-danger">*</span></label>
                  </div>
                  <div class="col-md-6">
                    <select
                      v-model="question.type"
                      class="form-select"
                      @change="onQuestionTypeChange(index, $event.target.value)"
                    >
                      <option value="typing">Typing</option>
                      <option value="radio">Multiple Choice</option>
                    </select>
                  </div>
                  <div class="col-md-3 text-end">
                    <div class="d-flex justify-content-end">
                      <button
                        v-if="module.questions.length > 1"
                        type="button"
                        @click="deleteQuestion(index)"
                        class="btn  btn-sm me-1"
                        title="Delete Question"
                      >
                        <i class="fas fa-trash-alt mx-1"></i>
                      </button>
                      <button
                        v-if="index === module.questions.length - 1"
                        type="button"
                        @click="addQuestion()"
                        class="btn btn-primary btn-sm"
                        title="Add Another Question"
                      >
                        <i class="mdi mdi-plus"></i> Add
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Radio Options Section -->
                <div v-if="question.type === 'radio'">
                  <div class="row mb-2">
                    <div class="col-md-3">
                      <label class="form-label fw-semibold ms-2">Options <span class="text-danger">*</span></label>
                    </div>
                    <div class="col-md-6">
                      <!-- Options List -->
                      <div v-for="(option, optIndex) in question.options" :key="optIndex" class="option-item mb-2">
                        <div class="d-flex align-items-center">
                          <div class="form-check me-2">
                            <input
                              type="radio"
                              :name="'question_' + index"
                              v-model="question.correct_option_index"
                              :value="optIndex"
                              class="form-check-input"
                              :id="'option_' + index + '_' + optIndex"
                              :class="{
                                'is-invalid': hasQuestionError(index, 'correct_option_index'),
                              }"
                            />
                          </div>
                          <div class="flex-grow-1">
                            <textarea
                              type="text"
                              class="form-control"
                              v-model="option.name"
                              :placeholder="'Enter option ' + (optIndex + 1)"
                              :class="{
                                'is-invalid': hasOptionError(index, optIndex),
                              }"
                            />
                          </div>
                          <button
                            v-if="question.options.length > 2"
                            type="button"
                            @click="deleteOption(index, optIndex)"
                            class="btn  btn-sm ms-2"
                            title="Remove Option"
                          >
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                        <v-errors
                          v-if="hasOptionError(index, optIndex)"
                          :vuelidateErrors="{
                            errors: getOptionErrors(index, optIndex),
                            value: 'Option text',
                          }"
                        ></v-errors>
                      </div>
                      
                      <!-- Add Option Button -->
                      <div class="d-flex align-items-center mt-1">
                        <button
                          type="button"
                          @click="addOption(index)"
                          class="btn btn-outline-secondary btn-sm me-2"
                        >
                          <i class="mdi mdi-plus"></i> Add Option
                        </button>
                        <small class="text-muted">Minimum 2 options required</small>
                      </div>
                      
                      <!-- Correct Answer Error -->
                      <v-errors
                        v-if="hasQuestionError(index, 'correct_option_index')"
                        :vuelidateErrors="{
                          errors: getQuestionErrors(index, 'correct_option_index'),
                          value: 'Correct answer',
                        }"
                        class="mt-1"
                      ></v-errors>
                    </div>
                  </div>
                </div>
                <hr>
              </div>

              <!-- Action Buttons -->
              <div class="row mt-3">
                <div class="col-12">
                  <div class="text-center">
                    
                    <!-- Cancel Button -->
                    <button
                      type="button"
                      class="btn btn-secondary me-3"
                      @click="$router.push({ name: 'after-month-trainer-question-assessment' })"
                    >
                      Cancel
                    </button>

                    <!-- Save Assessment Button -->
                    <button
                      v-if="hasPermissions(['self-assessment-question-create'])"
                      :disabled="loading"
                      type="button"
                      class="btn btn-success px-3"
                      @click="assessmentModuleCreate()"
                    >
                      <span
                        v-if="loading"
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {{ loading ? 'Saving...' : 'Save' }}
                    </button>

                  </div>
                </div>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useToast } from "vue-toastification";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import validationErrors from "../../../mixins/validationErrors";
import userHasPermissions from "../../../mixins/userHasPermissions";

export default {
  setup() {
    const toast = useToast();
    return { v$: useVuelidate(), toast };
  },
  components: {
    vSelect,
  },
  mixins: [validationErrors, userHasPermissions],
  data() {
    return {
      selected: {},
      moduleList: [],
      aseesment_modules: [],
      module: {
        name: "",
        assessment_duration: "30",
        questions: [
          {
            question: "",
            type: "typing", // default type
            options: [],
            correct_option_index: null,
          },
        ],
      },
      loading: false,
      isLoading: false,
      baseUrl: process.env.VUE_APP_BASE_URL,
    };
  },
  methods: {
    async assessmentModuleCreate() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) {
        this.toast.error("Please fill all required fields correctly!");
        return;
      }
      
      // Validate radio questions
      for (const question of this.module.questions) {
        if (question.type === 'radio') {
          // Check if at least 2 options
          if (question.options.length < 2) {
            this.toast.error(`Question "${question.question || 'Untitled'}" must have at least 2 options`);
            return;
          }
          
          // Check if a correct answer is selected
          if (question.correct_option_index === null || question.correct_option_index === undefined) {
            this.toast.error(`Please select a correct answer for question "${question.question || 'Untitled'}"`);
            return;
          }
          
          // Validate all options have text
          for (const option of question.options) {
            if (!option.name || !option.name.trim()) {
              this.toast.error("All option fields must be filled");
              return;
            }
          }
        }
      }
      
      // Prepare data for API according to backend structure
      const sendData = {
        module_id: this.module.name.id,
        assessment_duration: this.module.assessment_duration,
        questions: this.module.questions.map((q) => {
          const questionData = {
            question: q.question,
            type: q.type,
          };
          
          if (q.type === 'radio' && q.options && q.options.length > 0) {
            questionData.options = q.options.map((opt, index) => ({
              name: opt.name,
              is_correct: index === q.correct_option_index
            }));
          }
          
          return questionData;
        })
      };
      
      
      this.loading = true;

      await axios
        .post(`${this.baseUrl}admin/v2/self-assessment-questions`, sendData)
        .then(() => {
          this.$router.push({ name: "after-month-trainer-question-assessment" });
          this.toast.success("Successfully created Assessment Module.");
          this.loading = false;
        })
        .catch((error) => {
          if (error.response.status === 422) {
            this.toast.error("Validation Error! Please check all fields.");
            console.error("Validation errors:", error.response.data.errors);
          } else {
            this.toast.error("Something Went Wrong!");
          }
          this.loading = false;
        });
    },
    
    isSelectedModule(option) {
      // option.id comes from moduleList
      return !this.usedModuleIds.includes(option.id);
    },
      
    addQuestion() {
      this.module.questions.push({
        question: "",
        type: "typing",
        options: [],
        correct_option_index: null,
      });
    },
    
    deleteQuestion(index) {
      if (this.module.questions.length > 1) {
        this.module.questions.splice(index, 1);
      } else {
        // Reset the only question
        this.module.questions[0] = {
          question: "",
          type: "typing",
          options: [],
          correct_option_index: null,
        };
      }
    },
    
    onQuestionTypeChange(index, type) {
      if (type === 'radio') {
        // Initialize with two empty options for radio type
        this.module.questions[index].options = [
          { name: "" },
          { name: "" }
        ];
        this.module.questions[index].correct_option_index = null;
      } else {
        // Clear options for typing type
        this.module.questions[index].options = [];
        this.module.questions[index].correct_option_index = null;
      }
    },
    
    addOption(questionIndex) {
      this.module.questions[questionIndex].options.push({ name: "" });
    },
    
    deleteOption(questionIndex, optionIndex) {
      const options = this.module.questions[questionIndex].options;
      
      if (options.length <= 2) {
        this.toast.warning("Radio questions must have at least 2 options");
        return;
      }
      
      const isDeletingCorrectAnswer = optionIndex === this.module.questions[questionIndex].correct_option_index;
      
      // Remove the optionc
      options.splice(optionIndex, 1);
      
      // Update correct_option_index if needed
      if (isDeletingCorrectAnswer) {
        this.module.questions[questionIndex].correct_option_index = null;
      } else if (this.module.questions[questionIndex].correct_option_index > optionIndex) {
        // If the correct answer index is after the deleted one, decrement it
        this.module.questions[questionIndex].correct_option_index--;
      }
    },
    
    async getModule() {
      this.loading = true;
      await axios
        .get(`${this.baseUrl}admin/v1/modules`)
        .then((response) => {
          this.moduleList = response.data.data;
          this.loading = false;
        })
        .catch((error) => {
          this.toast.error("Something Went Wrong!");
          this.loading = false;
        });
    },

    async getQuestionAssessment() {
      this.loading = true;
      this.$Progress.start();
      await axios
        .get(`${this.baseUrl}admin/v2/self-assessment-questions`)
        .then((response) => {
          this.question_assessment = response.data;
          this.loading = false;
          this.$Progress.finish();
        })
        .catch((error) => {
          this.errors = error.response.data.errors;
          this.toast.error("Something Went Wrong!");
        });
    },
    
    // Helper methods for validation
    hasQuestionError(questionIndex, field) {
      if (!this.v$?.module?.questions?.$each?.$response?.$errors) return false;
      const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
      return questionErrors && questionErrors[field] && questionErrors[field].length > 0;
    },
    
    getQuestionErrors(questionIndex, field) {
      if (!this.v$?.module?.questions?.$each?.$response?.$errors) return [];
      const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
      return questionErrors && questionErrors[field] ? questionErrors[field] : [];
    },
    
    hasOptionError(questionIndex, optionIndex) {
      if (!this.v$?.module?.questions?.$each?.$response?.$errors) return false;
      const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
      if (!questionErrors || !questionErrors.options?.$each?.$response?.$errors) return false;
      const optionErrors = questionErrors.options.$each.$response.$errors[optionIndex];
      return optionErrors && optionErrors.name && optionErrors.name.length > 0;
    },
    
    getOptionErrors(questionIndex, optionIndex) {
      if (!this.v$?.module?.questions?.$each?.$response?.$errors) return [];
      const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
      if (!questionErrors || !questionErrors.options?.$each?.$response?.$errors) return [];
      const optionErrors = questionErrors.options.$each.$response.$errors[optionIndex];
      return optionErrors && optionErrors.name ? optionErrors.name : [];
    },
  },

  computed: {
    usedModuleIds() {
      return this.question_assessment.map(item => item.module_id);
    }
  },
  
  validations() {
    return {
      module: {
        name: { required },
        assessment_duration: { required },
        questions: {
          $each: {
            question: { required },
            type: { required },
            options: {
              $each: {
                name: { required }
              },
              // Custom validator for minimum 2 options when type is radio
              $validator: (value, { type }) => {
                if (type === 'radio') {
                  return value.length >= 2;
                }
                return true;
              },
              $message: 'Radio questions must have at least 2 options'
            },
            correct_option_index: {
              // Required only for radio type questions
              $validator: (value, { type, options }) => {
                if (type === 'radio') {
                  return value !== null && value !== undefined && value >= 0 && value < options.length;
                }
                return true;
              },
              $message: 'Please select a correct answer'
            }
          }
        }
      }
    };
  },
  
  async created() {
    await this.getModule();
    await this.getQuestionAssessment();
  },
};
</script>

<style scoped>
.del {
  color: rgb(126, 26, 26);
}
.del:hover {
  cursor: pointer;
}
.question-item:last-child {
  border-bottom: none !important;
}
.option-item:last-child {
  margin-bottom: 0 !important;
}
/* Reduced spacing */
.row.mb-4 { margin-bottom: 1rem !important; }
.row.mb-3 { margin-bottom: 0.75rem !important; }
.row.mb-2 { margin-bottom: 0.5rem !important; }
.mb-4 { margin-bottom: 1rem !important; }
.mb-3 { margin-bottom: 0.75rem !important; }
.mb-2 { margin-bottom: 0.5rem !important; }
.mt-3 { margin-top: 0.75rem !important; }
.mt-2 { margin-top: 0.5rem !important; }
.mt-1 { margin-top: 0.25rem !important; }
.pb-2 { padding-bottom: 0.5rem !important; }
.py-5 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
.py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
.py-1 { padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }
.px-4 { padding-left: 1rem !important; padding-right: 1rem !important; }
.px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
</style>